import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Package, Truck, Store, Leaf } from "lucide-react";
import { Layout } from "../components/Layout";
import { useI18n } from "../i18n";
import { eur } from "../lib/format";
import * as apiClient from "../api";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useI18n();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["product", slug], queryFn: () => apiClient.getProduct(slug) });

  if (isLoading) return <Layout><div className="text-center py-24 text-slate-400">{t("common.loading")}</div></Layout>;
  if (isError || !p) return <Layout><div className="text-center py-24"><p className="text-slate-500">Prodotto non trovato</p><Link to="/shop" className="mt-4 inline-block text-primary font-semibold">Torna allo Shop</Link></div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="product-detail">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary mb-6"><ArrowLeft className="h-4 w-4" /> Shop</Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="grid grid-cols-2 gap-3">
            {(p.images || []).slice(0, 4).map((img, i) => (
              <img key={i} src={img} alt={p.name} className={`rounded-2xl object-cover w-full ${i === 0 ? "col-span-2 h-72" : "h-36"}`} loading={i === 0 ? "eager" : "lazy"} />
            ))}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{p.destination} · {p.category}</div>
            <h1 className="mt-2 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-slate-900">{p.name}</h1>
            <p className="mt-3 text-slate-600">{p.short_description}</p>
            <div className="mt-5 flex items-center gap-3">
              {p.discount_price ? (
                <>
                  <span className="text-3xl font-semibold text-primary">{eur(p.discount_price)}</span>
                  <span className="text-lg text-slate-400 line-through">{eur(p.price)}</span>
                </>
              ) : <span className="text-3xl font-semibold text-primary">{eur(p.price)}</span>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {p.shipping_available && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1"><Truck className="h-3.5 w-3.5" /> Spedizione</span>}
              {p.pickup_available && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1"><Store className="h-3.5 w-3.5" /> Ritiro</span>}
              {p.is_food && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1"><Leaf className="h-3.5 w-3.5" /> Alimentare</span>}
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1"><Package className="h-3.5 w-3.5" /> {p.stock > 0 ? `${p.stock} disponibili` : "Esaurito"}</span>
            </div>
            <button disabled data-testid="product-add-cart" className="mt-6 w-full rounded-full bg-slate-200 text-slate-500 font-semibold px-6 py-3.5 cursor-not-allowed">
              Acquisti in arrivo
            </button>
            <p className="mt-2 text-xs text-center text-slate-400">Lo shop apre al lancio ufficiale.</p>

            {p.description && <div className="mt-8"><h3 className="font-heading font-semibold text-slate-900 mb-2">Descrizione</h3><p className="text-slate-600 whitespace-pre-line">{p.description}</p></div>}
            {p.is_food && (p.ingredients || p.allergens || p.conservation) && (
              <div className="mt-6 rounded-2xl bg-slate-50 p-5 text-sm space-y-2">
                <h3 className="font-heading font-semibold text-slate-900">Informazioni alimentari</h3>
                {p.ingredients && <p><span className="font-medium">Ingredienti:</span> {p.ingredients}</p>}
                {p.allergens && <p><span className="font-medium">Allergeni:</span> {p.allergens}</p>}
                {p.conservation && <p><span className="font-medium">Conservazione:</span> {p.conservation}</p>}
                {p.origin && <p><span className="font-medium">Origine:</span> {p.origin}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
